/** Small shared primitives, so screens stay about their own logic. */

import { type ComponentProps, type ReactNode, useState } from "react";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-800">{label}</span>
      {hint && <span className="mt-0.5 block text-xs text-gray-500">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function Input(props: ComponentProps<"input">) {
  return (
    <input
      {...props}
      className={
        "w-full rounded-md border border-gray-300 bg-surface px-3 py-2 text-sm shadow-xs " +
        "outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100 " +
        "disabled:bg-gray-50 disabled:text-gray-500 " +
        (props.className ?? "")
      }
    />
  );
}

/** A password input with an eye button that shows or hides what was typed. */
export function PasswordInput(props: Omit<ComponentProps<"input">, "type">) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        {...props}
        type={visible ? "text" : "password"}
        className={"pr-10 " + (props.className ?? "")}
      />
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        title={visible ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-gray-500 outline-none hover:text-gray-700 focus-visible:text-accent-600"
      >
        <svg
          viewBox="0 0 24 24"
          className="size-5"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
          <circle cx="12" cy="12" r="3" />
          {visible && <path d="M3 3l18 18" />}
        </svg>
      </button>
    </div>
  );
}

export function Textarea(props: ComponentProps<"textarea">) {
  return (
    <textarea
      {...props}
      className={
        "w-full rounded-md border border-gray-300 bg-surface px-3 py-2 font-mono text-sm shadow-xs " +
        "outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100 " +
        (props.className ?? "")
      }
    />
  );
}

export function Button({
  variant = "primary",
  ...props
}: ComponentProps<"button"> & { variant?: "primary" | "secondary" | "danger" }) {
  const base =
    "inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium " +
    "transition-colors disabled:cursor-not-allowed disabled:opacity-60";
  const styles =
    variant === "primary"
      ? "bg-accent-600 text-white hover:bg-accent-700 dark:hover:bg-accent-500"
      : variant === "danger"
        ? "bg-red-600 text-white hover:bg-red-700 dark:hover:bg-red-500"
        : "border border-gray-300 bg-surface text-gray-800 hover:bg-gray-50";

  return <button {...props} className={`${base} ${styles} ${props.className ?? ""}`} />;
}

export function Alert({ kind, children }: { kind: "error" | "success"; children: ReactNode }) {
  const styles =
    kind === "error"
      ? "border-red-200 bg-red-50 text-red-800"
      : "border-accent-100 bg-accent-50 text-accent-700";

  return (
    <div role="status" className={`rounded-md border px-3 py-2 text-sm ${styles}`}>
      {children}
    </div>
  );
}

export function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-surface p-5 shadow-xs">
      {title && <h2 className="mb-4 text-base font-semibold text-gray-900">{title}</h2>}
      {children}
    </section>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-500">
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-gray-300 border-t-accent-600"
      />
      {label}
    </div>
  );
}
